export type ApiHttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiEndpointDoc {
  method: ApiHttpMethod;
  path: string;
  description: string;
  note?: string;
}

export interface ApiEndpointGroup {
  title: string;
  description?: string;
  endpoints: ApiEndpointDoc[];
}

export const API_KEY_ACCESS_GROUPS: ApiEndpointGroup[] = [
  {
    title: "Startups",
    description: "List and manage burn charts you own or collaborate on.",
    endpoints: [
      {
        method: "GET",
        path: "/startups",
        description: "List startups accessible to the key (all account startups or only scoped startups).",
      },
      {
        method: "POST",
        path: "/startups",
        description: "Create a new startup burn chart.",
      },
      {
        method: "PUT",
        path: "/startups/:id",
        description:
          "Update startup metadata such as name, description, funding, timeline dates, and team settings.",
      },
      {
        method: "GET",
        path: "/startups/:id/complete",
        description:
          "Fetch a startup with all financial data: expenses, revenues, and timeline events in one response.",
      },
    ],
  },
  {
    title: "Expenses",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/expenses",
        description: "List all expenses for a startup.",
      },
      {
        method: "POST",
        path: "/startups/:id/expenses",
        description: "Create a new expense.",
      },
      {
        method: "PUT",
        path: "/startups/:id/expenses/:expenseId",
        description: "Update an existing expense.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/expenses/:expenseId",
        description: "Delete an expense.",
      },
      {
        method: "POST",
        path: "/startups/:id/expenses/:expenseId/revise",
        description:
          "Close the current expense segment and create a new forward segment from an effective date.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/expenses/:expenseId/revision",
        description: "Undo the latest expense revision in a segment chain.",
      },
    ],
  },
  {
    title: "Revenues",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/revenues",
        description: "List all revenue entries for a startup.",
      },
      {
        method: "POST",
        path: "/startups/:id/revenues",
        description: "Create a new revenue entry.",
      },
      {
        method: "PUT",
        path: "/startups/:id/revenues/:revenueId",
        description: "Update an existing revenue entry.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/revenues/:revenueId",
        description: "Delete a revenue entry.",
      },
      {
        method: "POST",
        path: "/startups/:id/revenues/:revenueId/revise",
        description:
          "Close the current revenue segment and create a new forward segment from an effective date.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/revenues/:revenueId/revision",
        description: "Undo the latest revenue revision in a segment chain.",
      },
    ],
  },
  {
    title: "Timeline events",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/events",
        description: "List timeline events (milestones, launches, assets, etc.).",
      },
      {
        method: "POST",
        path: "/startups/:id/events",
        description: "Create a timeline event.",
      },
      {
        method: "PUT",
        path: "/startups/:id/events/:eventId",
        description: "Update a timeline event.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/events/:eventId",
        description: "Delete a timeline event.",
      },
      {
        method: "POST",
        path: "/startups/:id/events/:eventId/revise",
        description:
          "Close the current asset segment and create a new forward segment from an effective date.",
        note: "Asset events only.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/events/:eventId/revision",
        description: "Undo the latest asset revision in a segment chain.",
      },
    ],
  },
  {
    title: "Team & invites",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/members",
        description: "List members, pending invites, and team settings for a startup.",
      },
      {
        method: "POST",
        path: "/startups/:id/invites",
        description: "Invite a Burn Chart user to collaborate by email.",
        note: "Startup owner only.",
      },
      {
        method: "POST",
        path: "/startups/:id/invites/accept",
        description: "Accept a pending invite for the authenticated user.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/members/:memberUserId",
        description: "Remove a collaborator from a startup.",
        note: "Startup owner only.",
      },
      {
        method: "GET",
        path: "/invites/pending",
        description: "List pending startup invites for the key owner.",
      },
    ],
  },
  {
    title: "AI chat",
    endpoints: [
      {
        method: "POST",
        path: "/startups/:id/chat",
        description: "Send a message to the BurnChart AI assistant for a startup.",
      },
      {
        method: "GET",
        path: "/startups/:id/conversations",
        description: "List past AI chat conversations for a startup.",
      },
    ],
  },
  {
    title: "Customers",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/customers",
        description: "List customer records linked to a startup.",
      },
      {
        method: "POST",
        path: "/startups/:id/customers",
        description: "Create a customer record.",
      },
      {
        method: "PUT",
        path: "/startups/:id/customers/:customerId",
        description: "Update a customer record.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/customers/:customerId",
        description: "Delete a customer record.",
      },
    ],
  },
  {
    title: "Customer projections",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/customer-projections",
        description: "List customer growth projections.",
      },
      {
        method: "POST",
        path: "/startups/:id/customer-projections",
        description: "Create a customer projection.",
      },
      {
        method: "PUT",
        path: "/startups/:id/customer-projections/:projectionId",
        description: "Update a customer projection.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/customer-projections/:projectionId",
        description: "Delete a customer projection.",
      },
    ],
  },
  {
    title: "Customer growth models",
    description: "Growth scenarios use the same underlying data as customer growth models.",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/customer-growth-models",
        description: "List customer growth models.",
      },
      {
        method: "POST",
        path: "/startups/:id/customer-growth-models",
        description: "Create a customer growth model.",
      },
      {
        method: "GET",
        path: "/startups/:id/customer-growth-scenarios",
        description: "List customer growth scenarios (alias of growth models).",
      },
      {
        method: "POST",
        path: "/startups/:id/customer-growth-scenarios",
        description: "Create a customer growth scenario (alias of growth models).",
      },
    ],
  },
  {
    title: "Customer acquisition costs",
    description: "Acquisition cost models use the same underlying data as customer acquisition costs.",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/customer-acquisition-costs",
        description: "List customer acquisition cost entries.",
      },
      {
        method: "POST",
        path: "/startups/:id/customer-acquisition-costs",
        description: "Create a customer acquisition cost entry.",
      },
      {
        method: "GET",
        path: "/startups/:id/acquisition-cost-models",
        description: "List acquisition cost models (alias of acquisition costs).",
      },
      {
        method: "POST",
        path: "/startups/:id/acquisition-cost-models",
        description: "Create an acquisition cost model (alias of acquisition costs).",
      },
    ],
  },
  {
    title: "Subscription tiers",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/subscription-tiers",
        description: "List subscription pricing tiers.",
      },
      {
        method: "POST",
        path: "/startups/:id/subscription-tiers",
        description: "Create a subscription tier.",
      },
      {
        method: "PUT",
        path: "/startups/:id/subscription-tiers/:tierId",
        description: "Update a subscription tier.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/subscription-tiers/:tierId",
        description: "Delete a subscription tier.",
      },
    ],
  },
  {
    title: "Capacity groups",
    description:
      "Shared customer ceilings for locations or operational limits. Link projections via capacityGroupId on customer projections; milestone events can increase group capacity.",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/capacity-groups",
        description: "List capacity groups for a startup.",
      },
      {
        method: "GET",
        path: "/startups/:id/capacity-groups/:groupId",
        description:
          "Fetch one capacity group with linked customer projections and capacity milestone events.",
        note: "Response includes linkedProjections (projections with capacityGroupId set to this group) and capacityEvents (timeline events that increase this group's capacity).",
      },
      {
        method: "POST",
        path: "/startups/:id/capacity-groups",
        description: "Create a capacity group.",
        note: "Fields: name (required), description (optional), maxCustomers (required, integer ≥ 1).",
      },
      {
        method: "PUT",
        path: "/startups/:id/capacity-groups/:groupId",
        description: "Update a capacity group.",
        note: "Fields: name, description, maxCustomers.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/capacity-groups/:groupId",
        description: "Delete a capacity group.",
      },
    ],
  },
  {
    title: "Business plan inputs",
    description:
      "Narrative context for AI business plan generation. Financial numbers are computed from the burn chart when you generate the plan. Authenticate with a Burn Chart API key (Bearer bc_…).",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/business-plan-context",
        description:
          "Read business plan inputs: problem/solution, ICP, traction, founding team, roadmap, funding narrative, global AI guidance, and per-section drafts (sectionDrafts).",
      },
      {
        method: "PUT",
        path: "/startups/:id/business-plan-context",
        description:
          "Update business plan inputs. Omitted fields are preserved. Providing sectionDrafts replaces the entire sectionDrafts map.",
        note:
          "Fields: problemStatement, solutionDescription, targetCustomer, valueProposition, marketCategory, geographicFocus, competitiveLandscape, differentiation, tractionHighlights, foundingTeam, milestonesAndRoadmap, fundingStage, fundingStageLabel, previousFundingRaised, previousInvestors, useOfFunds, fundingTimeline, generationGuidance, investorPitch, sectionDrafts.",
      },
      {
        method: "PATCH",
        path: "/startups/:id/business-plan-context",
        description:
          "Partial update with deep-merge for sectionDrafts — preferred when updating a single section draft or guidance entry.",
      },
    ],
  },
  {
    title: "Business plan",
    description: "Generate and read the investor-ready business plan document.",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/business-plan",
        description:
          "Fetch the latest generated business plan (sections, financialSnapshot with investorHighlights, companyProfile).",
      },
      {
        method: "POST",
        path: "/startups/:id/business-plan/generate",
        description:
          "Generate or regenerate the business plan from burn chart data and business plan inputs.",
        note: "Startup owner only. May take 30–60 seconds.",
      },
    ],
  },
  {
    title: "Investor links",
    description: "Password-protected share links for the investor portal.",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/investor-links",
        description: "List investor share links for a startup.",
        note: "Startup owner only.",
      },
      {
        method: "POST",
        path: "/startups/:id/investor-links",
        description: "Create an investor link (label, passcode, optional expiresAt). Returns share URL and token.",
        note: "Startup owner only.",
      },
      {
        method: "GET",
        path: "/startups/:id/investor-links/:linkId/share-url",
        description: "Get the shareable URL for an existing link.",
        note: "Startup owner only.",
      },
      {
        method: "POST",
        path: "/startups/:id/investor-links/:linkId/rotate-token",
        description: "Rotate the link token (invalidates old URLs).",
        note: "Startup owner only.",
      },
      {
        method: "PATCH",
        path: "/startups/:id/investor-links/:linkId",
        description: "Update link label, PIN, or expiry.",
        note: "Startup owner only.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/investor-links/:linkId",
        description: "Revoke an investor link.",
        note: "Startup owner only.",
      },
    ],
  },
  {
    title: "Investor comments",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/investor-comments",
        description: "List comments left by investors on shared links.",
      },
      {
        method: "POST",
        path: "/startups/:id/investor-comments/:commentId/replies",
        description: "Reply to an investor comment as the founder.",
      },
      {
        method: "POST",
        path: "/startups/:id/investor-comments/mark-read",
        description: "Mark all investor comments as read.",
      },
    ],
  },
  {
    title: "Team roster",
    description: "Founding team and key hires for investor view and business plan Team section.",
    endpoints: [
      {
        method: "GET",
        path: "/startups/:id/team-members",
        description: "List team members.",
      },
      {
        method: "POST",
        path: "/startups/:id/team-members",
        description: "Add a team member.",
      },
      {
        method: "PUT",
        path: "/startups/:id/team-members/:memberId",
        description: "Update a team member.",
      },
      {
        method: "DELETE",
        path: "/startups/:id/team-members/:memberId",
        description: "Remove a team member.",
      },
    ],
  },
];

export const FIREBASE_ONLY_ENDPOINTS: ApiEndpointDoc[] = [
  {
    method: "GET",
    path: "/auth/profile",
    description: "Read the signed-in user profile.",
  },
  {
    method: "POST",
    path: "/auth/profile",
    description: "Create a user profile (and optional first startup) during sign-up.",
  },
  {
    method: "PUT",
    path: "/auth/profile",
    description: "Update the signed-in user profile.",
  },
  {
    method: "GET",
    path: "/auth/api-keys",
    description: "List API keys for the signed-in user.",
  },
  {
    method: "POST",
    path: "/auth/api-keys",
    description: "Create a new API key.",
  },
  {
    method: "DELETE",
    path: "/auth/api-keys/:keyId",
    description: "Revoke an API key.",
  },
];

export const PUBLIC_API_ENDPOINTS: ApiEndpointDoc[] = [
  {
    method: "GET",
    path: "/",
    description: "API metadata and version info.",
  },
  {
    method: "GET",
    path: "/health",
    description: "Health check for monitoring.",
  },
];
